"use client";

import dynamic from "next/dynamic";
import Link from "next/link";
import type { DetailedMatchingResponse, MatchingResponse, PersonInput } from "@/lib/api/kundali";
import { reportOfferCopyFor } from "@/lib/report-offer-copy";
import SavedLinkCard from "@/components/SavedLinkCard";
import RatingPrompt from "@/components/RatingPrompt";
import { MATCH_REASONS } from "@/lib/feedback/resultReasons";
import { ValueMomentContactCapture } from "@/components/ValueMomentContactCapture";
import OfferImpression from "@/components/OfferImpression";
import { DeeperInsightsCTA } from "./DeeperInsightsCTA";

// The ONE source for the Gun Milan value surface. Rendered by the inline
// kundli-matching tool, /gun-milan, AND the /match/<id> share surface so the
// value moment and its intent-matched offer can never drift apart (rule 46:
// when a result relocates, the monetization travels with the exact same tree).

// Post-submission result components — loaded only when a result renders
const MatchingScoreCard = dynamic(
  () => import("@/components/matching/MatchingScoreCard").then((m) => m.MatchingScoreCard),
  { ssr: false, loading: () => null }
);
const KootaBreakdown = dynamic(
  () => import("@/components/matching/KootaBreakdown").then((m) => m.KootaBreakdown),
  { ssr: false, loading: () => null }
);
const MangalDoshaStatus = dynamic(
  () => import("@/components/matching/MangalDoshaStatus").then((m) => m.MangalDoshaStatus),
  { ssr: false, loading: () => null }
);
const DetailedMatchingReport = dynamic(
  () =>
    import("@/components/matching/DetailedMatchingReport").then((m) => m.DetailedMatchingReport),
  { ssr: false, loading: () => null }
);
const MatchingCrossSell = dynamic(
  () => import("@/components/matching/MatchingCrossSell").then((m) => m.MatchingCrossSell),
  { ssr: false, loading: () => null }
);
const PaidReportCTA = dynamic(() => import("@/components/PaidReportCTA"), {
  ssr: false,
  loading: () => null,
});

export type MatchingSurface = "tool" | "gun-milan" | "share";

export interface MatchingResultsPanelProps {
  result: MatchingResponse;
  personAData: PersonInput | null;
  personBData: PersonInput | null;
  defaultLanguage: "en" | "hi";
  detailedResult?: DetailedMatchingResponse | null;
  detailedLoading?: boolean;
  detailedError?: string | null;
  pdfLoading?: boolean;
  onGetDetailed?: () => void;
  onDownloadPdf?: () => void;
  /** /gun-milan keeps its fast free-tool flow; detailed report belongs to /kundli-matching. */
  showDetailedReport?: boolean;
  /** Inline tool flow resets the wizard; the /match share surface omits this
   * and gets a "Match Another Pair" link instead. */
  onReset?: () => void;
  /** Which surface renders the panel — sets offer analytics attribution. */
  surface: MatchingSurface;
}

export function MatchingResultsPanel({
  result,
  personAData,
  personBData,
  defaultLanguage,
  detailedResult,
  detailedLoading,
  detailedError,
  pdfLoading,
  onGetDetailed,
  onDownloadPdf,
  onReset,
  surface,
  showDetailedReport = true,
}: MatchingResultsPanelProps) {
  return (
    <div className="space-y-6">
      <MatchingScoreCard result={result} />
      <KootaBreakdown result={result} />
      <MangalDoshaStatus result={result} />
      <MarriageReportOffer
        personAData={personAData}
        personBData={personBData}
        defaultLanguage={defaultLanguage}
        surface={surface}
      />
      <ResultRatingPrompt defaultLanguage={defaultLanguage} />
      <MatchingCrossSell result={result} />
      <Recommendation recommendation={result.recommendation} />
      <SavedMatchCapture result={result} defaultLanguage={defaultLanguage} surface={surface} />
      <DetailedReportSlot
        result={result}
        detailedResult={detailedResult}
        detailedLoading={detailedLoading}
        detailedError={detailedError}
        pdfLoading={pdfLoading}
        onGetDetailed={onGetDetailed}
        onDownloadPdf={onDownloadPdf}
        showDetailedReport={showDetailedReport}
      />
      <ResultActions onReset={onReset} surface={surface} />
    </div>
  );
}

function ResultRatingPrompt({ defaultLanguage }: { defaultLanguage: "en" | "hi" }) {
  const hi = defaultLanguage === "hi";
  return (
    <div>
      <RatingPrompt
        source={"tool_result" as never}
        mode="thumbs+reasons"
        reasons={MATCH_REASONS}
        prompt={hi ? "Kya yeh milan result sahi laga?" : "Did this matching result feel right?"}
        context={{
          surface: "matching_result",
          path: typeof window !== "undefined" ? window.location.pathname : null,
        }}
      />
    </div>
  );
}

function SavedMatchCapture({
  result,
  defaultLanguage,
  surface,
}: {
  result: MatchingResponse;
  defaultLanguage: "en" | "hi";
  surface: MatchingSurface;
}) {
  if (!result.match_id) return null;

  const hi = defaultLanguage === "hi";
  return (
    <>
      <SavedLinkCard
        path={`/match/${result.match_id}${hi ? "?lang=hi" : ""}`}
        heading={hi ? "Yeh milan result save ho gaya hai" : "This match is saved"}
        body={
          hi
            ? "Link family ke saath share karein ya kabhi bhi dobara kholein — dono kundali details dobara bharne ki zaroorat nahi."
            : "Share the link with your family or return to it anytime — no need to re-enter both birth details."
        }
        shareLabel={hi ? "Link share karein" : "Share link"}
        copiedLabel={hi ? "Link copy ho gaya" : "Link copied"}
        analyticsEvent="match_share_clicked"
        analyticsProps={{ match_id: result.match_id, language: defaultLanguage, surface }}
      />
      <ValueMomentContactCapture
        target={{ type: "match", id: result.match_id }}
        language={defaultLanguage}
        placement={contactCapturePlacement(surface)}
      />
    </>
  );
}

function DetailedReportSlot({
  result,
  detailedResult,
  detailedLoading,
  detailedError,
  pdfLoading,
  onGetDetailed,
  onDownloadPdf,
  showDetailedReport,
}: {
  result: MatchingResponse;
  detailedResult?: DetailedMatchingResponse | null;
  detailedLoading?: boolean;
  detailedError?: string | null;
  pdfLoading?: boolean;
  onGetDetailed?: () => void;
  onDownloadPdf?: () => void;
  showDetailedReport: boolean;
}) {
  const canShowDetailedReport =
    showDetailedReport &&
    typeof onGetDetailed === "function" &&
    typeof onDownloadPdf === "function";
  const getDetailed = canShowDetailedReport ? onGetDetailed : undefined;
  const downloadPdf = canShowDetailedReport ? onDownloadPdf : undefined;

  if (getDetailed && !detailedResult) {
    return (
      <DeeperInsightsCTA
        detailedLoading={detailedLoading ?? false}
        detailedError={detailedError ?? null}
        onGetDetailed={getDetailed}
      />
    );
  }

  if (downloadPdf && detailedResult) {
    return (
      <DetailedMatchingReport
        result={result}
        detailedResult={detailedResult}
        pdfLoading={pdfLoading ?? false}
        onDownloadPdf={downloadPdf}
      />
    );
  }

  return null;
}

function offerAnalytics(defaultLanguage: "en" | "hi", surface: MatchingSurface) {
  if (surface === "share") {
    return {
      source: "match-share",
      sourcePath: "/match",
      placement: "match_share_results_primary",
    };
  }
  if (surface === "gun-milan") {
    return defaultLanguage === "hi"
      ? {
          source: "hi-gun-milan",
          sourcePath: "/hi/gun-milan",
          placement: "hi_gun_milan_results_primary",
        }
      : {
          source: "gun-milan",
          sourcePath: "/gun-milan",
          placement: "gun_milan_results_primary",
        };
  }
  return defaultLanguage === "hi"
    ? {
        source: "hi-kundli-matching",
        sourcePath: "/hi/kundli-matching",
        placement: "kundli_matching_results_primary",
      }
    : {
        source: "kundli-matching",
        sourcePath: "/kundli-matching",
        placement: "kundli_matching_results_primary",
      };
}

function MarriageReportOffer({
  personAData,
  personBData,
  defaultLanguage,
  surface,
}: {
  personAData: PersonInput | null;
  personBData: PersonInput | null;
  defaultLanguage: "en" | "hi";
  surface: MatchingSurface;
}) {
  const planCopy = reportOfferCopyFor("marriage_report", defaultLanguage);
  const analytics = offerAnalytics(defaultLanguage, surface);
  const routeRankScore = surface === "gun-milan" ? 95 : 96;
  const routeRankReason =
    surface === "gun-milan"
      ? "matching_result_marriage_report"
      : "kundli_matching_result_marriage_report";
  // Narrow the nullables directly (not via `ready`) so TS knows they're non-null
  // for matchingReportPerson below — equivalent to `!ready`.
  if (!personAData || !personBData) return null;
  return (
    <>
      <OfferImpression
        planId="marriage_report"
        sourcePath={analytics.sourcePath}
        intent="chart_report"
        placement={analytics.placement}
        customerProblem="marriage"
        urgency="high"
        confidence={surface === "gun-milan" ? 0.9 : 0.92}
        routeRankScore={routeRankScore}
        routeRankReason={routeRankReason}
      />
      <PaidReportCTA
        planId="marriage_report"
        priceInr={planCopy.priceInr}
        headline={
          defaultLanguage === "hi"
            ? `₹${planCopy.priceInr} Kundali Milan Report paayein`
            : `Get the ₹${planCopy.priceInr} Marriage Matching Report`
        }
        subhead={
          defaultLanguage === "hi"
            ? "Family-shareable PDF: full Gun Milan, Mangal Dosh, D9/Navamsha, 5-year dasha timing, risks, strengths aur upay."
            : "Family-shareable PDF with full Gun Milan, Mangal Dosha, D9/Navamsha compatibility, 5-year dasha timing, risks, strengths, and practical remedies."
        }
        language={defaultLanguage}
        source={analytics.source}
        analyticsContext={{
          placement: analytics.placement,
          checkoutChannel: "ishvaram_report",
          commerceSystem: "ishvaram",
          intent: "chart_report",
          sourcePath: analytics.sourcePath,
          customerProblem: "marriage",
          urgency: "high",
          confidence: surface === "gun-milan" ? 0.9 : 0.92,
          routeRankScore,
          routeRankReason,
          rankedOfferOrder: ["marriage_report"],
        }}
        reportInputs={{
          person_a: matchingReportPerson(personAData),
          person_b: matchingReportPerson(personBData),
          language: defaultLanguage,
        }}
      />
    </>
  );
}

function matchingReportPerson(person: PersonInput) {
  return {
    name: person.name,
    birth_date: person.birth_date,
    birth_time: person.birth_time || null,
    birth_place: person.birth_place,
  };
}

function contactCapturePlacement(surface: MatchingSurface) {
  return surface === "gun-milan"
    ? "gun-milan-tool-value-moment"
    : `kundli-matching-${surface}-value-moment`;
}

function Recommendation({ recommendation }: { recommendation?: string | null }) {
  if (!recommendation) return null;

  return (
    <div className="card-sacred p-6 sm:p-8 bg-ish-accent-light">
      <h2 className="text-[16px] font-bold mb-3 text-ish-accent">Recommendation</h2>
      <p className="text-[14px] leading-relaxed text-ish-text-body">{recommendation}</p>
    </div>
  );
}

function ResultActions({ onReset, surface }: { onReset?: () => void; surface: MatchingSurface }) {
  const primary =
    surface === "gun-milan"
      ? { href: "/kundli-matching/", label: "Full Kundli Matching Report \u2192" }
      : { href: "/free-kundali", label: "Get Full Kundali Reading \u2192" };
  return (
    <div className="flex flex-col sm:flex-row gap-3 justify-center">
      <Link
        href={primary.href}
        className="btn-sacred text-center font-semibold py-3 px-6 rounded-lg text-[14px]"
      >
        {primary.label}
      </Link>
      {onReset ? (
        <button
          onClick={onReset}
          className="text-center font-semibold py-3 px-6 rounded-lg text-[14px] transition-all duration-200 text-ish-text-muted border border-ish-border"
        >
          Match Another Pair
        </button>
      ) : (
        <Link
          href="/kundli-matching"
          className="text-center font-semibold py-3 px-6 rounded-lg text-[14px] transition-all duration-200 text-ish-text-muted border border-ish-border"
        >
          Match Another Pair
        </Link>
      )}
    </div>
  );
}
